import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */

const KWIKPASS_MODAL_SELECTOR = '.container[class*="svelte-"]';

function isElementVisible(el) {
  if (!el) return false;

  const cs = window.getComputedStyle(el);
  if (cs.display === 'none' || cs.visibility === 'hidden' || Number(cs.opacity || 1) === 0) return false;

  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

function isKwikPassModalOpen() {
  const el = document.querySelector(KWIKPASS_MODAL_SELECTOR);
  return isElementVisible(el);
}

class CartDrawerComponent extends DialogComponent {
  /** @type {MutationObserver | null} */
  __kwikpassObserver = null;

  /** @type {(() => void) | null} */
  __onKwikPassOpenLogin = null;

  connectedCallback() {
    super.connectedCallback();

    // Existing behavior
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    // KwikPass: close cart when login modal opens (event + observer backup)
    if (!this.__onKwikPassOpenLogin) {
      this.__onKwikPassOpenLogin = () => {
        try {
          // Use the component’s real close API
          close();
          this.closeDialog();
        } catch (_) {
          try {
            // fallback, in case closeDialog throws for some reason
            if (typeof this.close === 'function') this.close();
          } catch (_) {}
        }
      };

      // If you dispatch this event before opening login, cart closes immediately
      document.addEventListener('kwikpass:open-login', this.__onKwikPassOpenLogin);

      // Backup: if KwikPass modal appears without the event, still close cart
      this.__kwikpassObserver = new MutationObserver(() => {
        if (isKwikPassModalOpen()) this.__onKwikPassOpenLogin?.();
      });

      this.__kwikpassObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Existing behavior cleanup
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    // KwikPass cleanup
    if (this.__onKwikPassOpenLogin) {
      document.removeEventListener('kwikpass:open-login', this.__onKwikPassOpenLogin);
      this.__onKwikPassOpenLogin = null;
    }

    if (this.__kwikpassObserver) {
      try {
        this.__kwikpassObserver.disconnect();
      } catch (_) {}
      this.__kwikpassObserver = null;
    }
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     * IMPORTANT: ensure correct "this" by wrapping in an arrow function
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.closeDialog(), { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}